/**
 * `/o/:orderNo` —— WhatsApp 通知嘅 **deep link 入口**（穩定 URL：`<settings.app.base_url>/o/<單號>`）
 *
 * ⚠️ 連結**唔帶任何 token**、**唔准繞登入**：未登入時 App 殼會先出登入頁，登入後才見到內容
 *    （證據相一樣只可以經已登入 session 讀 —— `rules §七 H5`）。
 * 流程：`?orderNo=` 反查（`GET /api/outbound-orders`）→ 搵到就顯示狀態 ＋ 入去；
 *    · `sent`（＝已通知、等執貨）→ 直接擺「去執貨清單」掣（現場最先想做嘅事）
 *    · `acknowledged`（＝已執貨、等交收）→ 擺「**去交收**」掣（`decisions/018`；倉同事由 WA 連結入）
 *    · 其他狀態 → 睇詳情
 * ⚠️ 供應商冇出庫權限（後端 403）→ 一律顯示「冇權限」，唔會漏任何單嘅資料。
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { btnGhost, btnPrimary, card, errText, hint as hintStyle } from '@coeldery/ui-kit';
import { OutboundStatusChip } from '../components/OutboundStatusChip';
import { ApiError, api, type Me, type OutboundOrder } from '../lib/api';

type Phase = 'loading' | 'found' | 'missing' | 'noAccess' | 'error';

export default function OrderLink({ user }: { user: Me }) {
  const { t } = useTranslation();
  const { orderNo = '' } = useParams();
  const [order, setOrder] = useState<OutboundOrder | null>(null);
  const [phase, setPhase] = useState<Phase>('loading');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const res = await api.outboundOrders({ orderNo });
        if (!alive) return;
        const found = (res.orders ?? []).find((o) => o.orderNo === orderNo) ?? null;
        setOrder(found);
        setPhase(found ? 'found' : 'missing');
      } catch (err) {
        if (!alive) return;
        if (err instanceof ApiError && err.status === 403) setPhase('noAccess');
        else {
          setError(err instanceof ApiError ? err.message : t('common.loadFailed'));
          setPhase('error');
        }
      }
    })();
    return () => {
      alive = false;
    };
  }, [orderNo, t]);

  /** 可以執貨＝已通知（`sent`）＋ 唔係供應商（後端一樣把關） */
  const canPick = order?.status === 'sent' && user.role !== 'supplier';
  /**
   * ⭐ 執完貨、等交收（`acknowledged`）→ 直接畀「去交收」掣（`decisions/018`；
   *    倉同事平時就係撳 WhatsApp 嗰條連結入嚟，唔應該要佢再自己搵掣）
   */
  const canHandOver = order?.status === 'acknowledged' && user.role !== 'supplier';

  return (
    <section style={{ ...card, padding: '16px' }}>
      <h2 style={{ margin: '0 0 8px', fontSize: '17px', fontWeight: 800 }}>
        {t('order.linkTitle', { orderNo })}
      </h2>
      <p style={hintStyle}>{t('order.linkBody')}</p>

      {phase === 'loading' && <p style={hintStyle}>{t('order.lookingUp')}</p>}

      {phase === 'found' && order && (
        <>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', margin: '8px 0' }}>
            <strong>{order.orderNo}</strong>
            <OutboundStatusChip status={order.status} />
            <span style={hintStyle}>{t('out.pickupAt')}：{order.pickupAt ?? '—'}</span>
          </div>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            {canPick && (
              <Link
                to={`/outbound/${order.id}/picklist`}
                style={{ ...btnPrimary, display: 'inline-flex', alignItems: 'center', textDecoration: 'none' }}
              >
                {t('order.startPick')}
              </Link>
            )}
            {canHandOver && (
              <Link
                to={`/outbound/${order.id}`}
                style={{ ...btnPrimary, display: 'inline-flex', alignItems: 'center', textDecoration: 'none' }}
              >
                {t('out.goHandOver')}
              </Link>
            )}
            <Link
              to={`/outbound/${order.id}`}
              style={{ ...btnGhost, display: 'inline-flex', alignItems: 'center', textDecoration: 'none' }}
            >
              {t('order.openOrder')}
            </Link>
          </div>
        </>
      )}

      {(phase === 'missing' || phase === 'noAccess' || phase === 'error') && (
        <p style={phase === 'error' ? errText : { ...hintStyle, fontWeight: 700 }} role="alert">
          {phase === 'missing'
            ? t('order.notFound', { orderNo })
            : phase === 'noAccess'
              ? t('order.noAccess')
              : error}
        </p>
      )}

      {phase !== 'found' && (
        <Link
          to="/"
          style={{ ...btnPrimary, display: 'inline-flex', alignItems: 'center', textDecoration: 'none', marginTop: '10px' }}
        >
          {t('order.goHome')}
        </Link>
      )}
    </section>
  );
}

